import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Legend } from 'recharts';
import { cn } from '../../../utils/classnames';
import { formatCurrency } from '../../../utils/formatters';

const DEFAULT_SERIES = [
  { key: 'totalFinancialProperties', label: 'Patrimônio Financeiro', color: '#2D86B4' },
  { key: 'totalFixedProperties', label: 'Patrimônio Imobilizado', color: '#5BB2D7' },
  { key: 'totalPension', label: 'Previdência', color: '#7AC3E0' },
  { key: 'totalInsurances', label: 'Seguros', color: '#9FD4E9' },
  { key: 'totalBtg', label: 'Investimentos BTG', color: '#143145' },
];

const BarRoundedChartStack = ({
  data = [],
  labelKey = 'label',
  series = DEFAULT_SERIES,
  className = '',
  maxBarSize = 50,
  showLegend = true,
}) => {
  const prepared = useMemo(() => {
    return (data || []).map((d) => {
      const total = series.reduce((acc, s) => acc + (Number(d[s.key]) || 0), 0);
      return { ...d, __total: total };
    });
  }, [data, series]);

  const topSeriesKey = series[series.length - 1]?.key;

  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const raw = payload[0]?.payload || {};
    const isPlaceholder = label === '...' || raw.year == null;

    if (isPlaceholder) {
      return (
        <div className="rounded-lg bg-white shadow-lg px-3 py-2 border border-blueUltraLight/40">
          <div className="text-sm font-semibold text-blueDarker mb-1">Sem dados</div>
        </div>
      );
    }

    const total = series.reduce((acc, s) => acc + (Number(raw[s.key]) || 0), 0);

    return (
      <div className="rounded-lg bg-white shadow-lg px-3 py-2 border border-blueUltraLight/40">
        <div className="text-sm font-semibold text-blueDarker mb-1">{label}</div>
        {series.map((s) => {
          const val = Number(raw[s.key]) || 0;
          if (val === 0) return null;
          return (
            <div key={s.key} className="flex items-center justify-between gap-6 text-sm">
              <span className="flex items-center gap-2">
                <span className="inline-block rounded-full" style={{ width: 8, height: 8, background: s.color }} />
                <span className="text-blueDark">{s.label}</span>
              </span>
              <span className="text-blueDarker font-medium">{formatCurrency(val)}</span>
            </div>
          );
        })}
        <div className="mt-2 border-t border-blueUltraLight/40 pt-1 flex items-center justify-between text-sm">
          <span className="text-blueDark">Total</span>
          <span className="text-blueDarker font-semibold">{formatCurrency(total)}</span>
        </div>
      </div>
    );
  };

  const renderTotalLabel = ({ x, y, width, height, value, index, viewBox, payload }) => {
    const isPlaceholder = payload?.[labelKey] === '...';

    // centro da barra
    const cx = width ? x + width / 2 : x;

    if (value === 0 || isPlaceholder) {
      // Bar de altura 0 => y = linha-base. Posiciona "Sem dados" um pouco acima do eixo X
      const cy = (y ?? 0) - 6; // sobe 6px
      return (
        <text
          textAnchor="middle"
          x={cx - width}
          y={cy}
          style={{ fontSize: 12, fontWeight: 400, fill: '#143145', fontFamily: 'Jost, sans-serif' }}
        >
          Sem dados
        </text>
      );
    }

    // Barra com valor: escreve acima do topo
    const cy = (y ?? 0) - 8;
    return (
      <text
        x={cx - width}
        y={cy}
        textAnchor="middle"
        style={{ fontSize: 12, fontWeight: 400, fill: '#143145', fontFamily: 'Jost, sans-serif' }}
      >
        {formatCurrency(value)}
      </text>
    );
  };

  return (
    <div className={cn('w-full h-96', className)}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={prepared} margin={{ top: 24, right: 0, left: 30, bottom: 0 }}>
          <XAxis
            dataKey={labelKey}
            axisLine={false}
            tickLine={false}
            interval={0} // mostra todos
            tickMargin={10} // espaço entre eixo e rótulo
            tick={{ fontSize: 12, fontWeight: 600, fill: '#143145', dx: -25 }}
          />
          <YAxis hide />
          <Tooltip content={renderTooltip} cursor={{ fill: 'transparent' }} />
          {showLegend && (
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              wrapperStyle={{ top: 8, right: 8 }}
              formatter={(value) => {
                const s = series.find((x) => x.key === value);
                return <span className="text-blueDark text-sm">{s?.label || value}</span>;
              }}
              payload={series.map((s) => ({
                value: s.key,
                type: 'circle',
                id: s.key,
                color: s.color,
              }))}
            />
          )}

          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              stackId="patrimony"
              fill={s.color}
              maxBarSize={maxBarSize}
              radius={s.key === topSeriesKey ? [12, 12, 0, 0] : [0, 0, 0, 0]}
              isAnimationActive={false}
            />
          ))}

          {/* Label do total no topo da pilha — mostra "sem dados" nas barras placeholder */}
          <Bar dataKey="__total" stackId="label-only" fill="transparent" isAnimationActive={false}>
            <LabelList dataKey="__total" content={renderTotalLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarRoundedChartStack;
